import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Table, Badge, Modal } from 'react-bootstrap';
import { getUsers, getAllSymbols, createManualBulkTrades, getManualTrades, getBulkTradeById, closeBulkTradeOrder, deleteBulkTrade, deleteTrade } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const ManualTrades = () => {
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [users, setUsers] = useState([]);
    const [symbols, setSymbols] = useState([]);
    const [selectedBulkTrade, setSelectedBulkTrade] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0
    });

    const [formData, setFormData] = useState({
        type: 'crypto',
        symbol: '',
        openValue: '',
        openDate: new Date().toISOString().split('T')[0],
        openTime: '',
        userTrades: [],
        status: '',
        search: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    const [closeFormData, setCloseFormData] = useState({
        bulkOrderId: '',
        closeValue: '',
        closeDate: new Date().toISOString().split('T')[0],
        closeTime: new Date().toTimeString().slice(0, 8)
    });

    const [bulkTrades, setBulkTrades] = useState([]);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchSymbols(formData.type);
    }, [formData.type]);

    useEffect(() => {
        fetchBulkTrades();
    }, [
        pagination.page,
        pagination.limit,
        formData.status,
        formData.search,
        formData.sortBy,
        formData.sortOrder,
        formData.type
    ]);

    // Inject custom styles
    useEffect(() => {
        const manualTradesStyles = `
            .btn-icon-xxs {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
            }
            .fs-12 {
                font-size: 12px;
            }
        `;

        const styleId = 'manual-trades-custom-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = manualTradesStyles;
            document.head.appendChild(style);
        }
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, []);

    const fetchSymbols = async (type) => {
        try {
            const response = await getAllSymbols({ type });
            if (response?.success && Array.isArray(response.data)) {
                setSymbols(response.data);
                setFormData(prev => ({ ...prev, symbol: '' }));
            }
        } catch (error) {
            console.error('Error fetching symbols:', error);
            alert(error.message || 'Failed to fetch symbols');
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await getUsers({ page: 1, limit: 1000 });
            if (response.success && response.data) {
                setUsers(response.data.users || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            alert('Failed to fetch users');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUserTradeAdd = () => {
        setFormData(prev => ({
            ...prev,
            userTrades: [
                ...prev.userTrades,
                { userId: '', quantity: '', direction: 'buy' }
            ]
        }));
    };

    const handleUserTradeRemove = (index) => {
        setFormData(prev => ({
            ...prev,
            userTrades: prev.userTrades.filter((_, i) => i !== index)
        }));
    };

    const handleUserTradeChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            userTrades: prev.userTrades.map((trade, i) => 
                i === index ? { ...trade, [field]: value } : trade
            )
        }));
    };

    const handleCreateTrades = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);

            const payload = {
                type: formData.type,
                symbol: formData.symbol,
                openValue: parseFloat(formData.openValue),
                openDate: formData.openDate,
                openTime: formData.openTime,
                userTrades: formData.userTrades.map(trade => ({
                    userId: trade.userId,
                    quantity: parseFloat(trade.quantity),
                    direction: trade.direction
                }))
            };

            const response = await createManualBulkTrades(payload);

            if (response.success) {
                alert('Bulk trades created successfully');
                setShowCreateModal(false);
                setFormData({
                    type: 'crypto',
                    symbol: '',
                    openValue: '',
                    openDate: new Date().toISOString().split('T')[0],
                    openTime: '',
                    userTrades: []
                });
                fetchBulkTrades();
            } else {
                throw new Error(response.message || 'Failed to create trades');
            }
        } catch (error) {
            console.error('Error creating trades:', error);
            alert(error.message || 'Failed to create trades');
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseTrades = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);

            const response = await closeBulkTradeOrder(
                closeFormData.bulkOrderId,
                closeFormData.closeValue,
                closeFormData.closeDate,
                closeFormData.closeTime
            );

            if (response.success) {
                alert(response.message || 'Bulk trades closed successfully');
                setShowCloseModal(false);
                setCloseFormData({
                    bulkOrderId: '',
                    closeValue: '',
                    closeDate: new Date().toISOString().split('T')[0],
                    closeTime: new Date().toTimeString().slice(0, 8)
                });
                fetchBulkTrades();
                if (selectedBulkTrade?.bulkOrderId === closeFormData.bulkOrderId) {
                    fetchBulkTradeDetails(closeFormData.bulkOrderId);
                }
            } else {
                throw new Error(response.message || 'Failed to close trades');
            }
        } catch (error) {
            console.error('Error closing trades:', error);
            alert(error.message || 'Failed to close trades');
        } finally {
            setProcessing(false);
        }
    };

    const fetchBulkTrades = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(formData.status && { status: formData.status }),
                ...(formData.search && { search: formData.search }),
                sortBy: formData.sortBy,
                sortOrder: formData.sortOrder,
                ...(formData.type && { type: formData.type })
            }).toString();

            const response = await getManualTrades({
                page: pagination.page,
                limit: pagination.limit,
                ...(formData.status && { status: formData.status }),
                ...(formData.search && { search: formData.search }),
                sortBy: formData.sortBy,
                sortOrder: formData.sortOrder,
                ...(formData.type && { type: formData.type })
            });
            
            if (response?.success && response?.data?.trades) {
                // Group trades by bulkOrderId
                const bulkTradesMap = response.data.trades.reduce((acc, trade) => {
                    if (!acc[trade.bulkOrderId]) {
                        acc[trade.bulkOrderId] = {
                            bulkOrderId: trade.bulkOrderId,
                            type: trade.type,
                            symbol: trade.symbol,
                            openValue: trade.entryPrice,
                            closeValue: trade.exitPrice,
                            status: trade.status,
                            createdAt: trade.createdAt,
                            summary: {
                                totalTrades: 0,
                                totalProfitLoss: 0
                            }
                        };
                    }
                    acc[trade.bulkOrderId].summary.totalTrades++;
                    acc[trade.bulkOrderId].summary.totalProfitLoss += trade.profitLoss || 0;
                    return acc;
                }, {});

                // Convert map to array
                const formattedBulkTrades = Object.values(bulkTradesMap);
                setBulkTrades(formattedBulkTrades);

                // Update pagination
                if (response.data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.data.pagination.total || 0,
                        page: response.data.pagination.page || 1,
                        limit: response.data.pagination.limit || 10
                    }));
                }
            } else {
                setBulkTrades([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            console.error('Error fetching bulk trades:', error);
            alert('Failed to fetch bulk trades');
            setBulkTrades([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    };

    const fetchBulkTradeDetails = async (bulkOrderId) => {
        try {
            setLoading(true);
            const response = await getBulkTradeById(bulkOrderId);
            if (response.success && response.data) {
                setSelectedBulkTrade(response.data);
                setShowDetailsModal(true);
            }
        } catch (error) {
            console.error('Error fetching trade details:', error);
            alert(error.message || 'Failed to fetch trade details');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'warning',
            completed: 'success',
            cancelled: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const getDirectionBadge = (direction) => {
        return direction === 'buy' ? 'success' : 'danger';
    };

    const handleDelete = async (bulkOrderId) => {
        const confirmed = window.confirm(
            "Are you sure? Once deleted, you will not be able to recover this bulk trade and all associated user trades!"
        );

        if (!confirmed) return;

        try {
            const response = await deleteBulkTrade(bulkOrderId);
            
            if (response.success) {
                alert(response.message || "Bulk trade deleted successfully");
                fetchBulkTrades();
            } else {
                throw new Error(response.message || 'Failed to delete bulk trade');
            }
        } catch (error) {
            console.error('Error deleting bulk trade:', error);
            alert(error.message || "Failed to delete bulk trade");
        }
    };

    const handleDeleteIndividualTrade = async (tradeId) => {
        const confirmed = window.confirm(
            "Are you sure? Once deleted, you will not be able to recover this trade!"
        );

        if (!confirmed) return;

        try {
            const response = await deleteTrade(tradeId);
            
            if (response.success) {
                alert(response.message || "Trade deleted successfully");
                fetchBulkTrades();
                if (selectedBulkTrade?.bulkOrderId) {
                    fetchBulkTradeDetails(selectedBulkTrade.bulkOrderId);
                }
            } else {
                throw new Error(response.message || 'Failed to delete trade');
            }
        } catch (error) {
            console.error('Error deleting trade:', error);
            alert(error.message || "Failed to delete trade");
        }
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Manual Trades" sidebutton={false} />

            <div className="row">
                <div className="col-xl-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="card-title mb-0">Manual Trades</h4>
                                    <small className="text-muted">Manage bulk manual trades</small>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        className="btn-rounded"
                                        onClick={() => fetchBulkTrades()}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <i className="fa fa-refresh me-2"></i>
                                        )}
                                        Refresh
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        className="btn-rounded"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <i className="fa fa-plus me-2"></i>Create Bulk Trade
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <div className="row">
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">All</option>
                                                <option value="crypto">Crypto</option>
                                                <option value="forex">Forex</option>
                                                <option value="gold">Gold</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">All</option>
                                                <option value="active">Active</option>
                                                <option value="completed">Completed</option>
                                                <option value="cancelled">Cancelled</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Search</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="search"
                                                value={formData.search}
                                                onChange={handleInputChange}
                                                placeholder="Search by ID or symbol..."
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Sort By</Form.Label>
                                            <div className="d-flex">
                                                <Form.Select
                                                    className="me-2"
                                                    name="sortBy"
                                                    value={formData.sortBy}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="createdAt">Created Date</option>
                                                    <option value="symbol">Symbol</option>
                                                    <option value="type">Type</option>
                                                </Form.Select>
                                                <Form.Select
                                                    name="sortOrder"
                                                    value={formData.sortOrder}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="asc">Asc</option>
                                                    <option value="desc">Desc</option>
                                                </Form.Select>
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table className="table table-responsive-md table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th><strong>Bulk Order ID</strong></th>
                                                <th><strong>Type/Symbol</strong></th>
                                                <th><strong>Open Value</strong></th>
                                                <th><strong>Close Value</strong></th>
                                                <th><strong>Status</strong></th>
                                                <th><strong>Total P/L</strong></th>
                                                <th><strong>Actions</strong></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!Array.isArray(bulkTrades) || bulkTrades.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center">
                                                        <div className="d-flex justify-content-center align-items-center flex-column py-5">
                                                            <i className="fa fa-chart-line fa-3x text-muted mb-3"></i>
                                                            <h4 className="text-muted">No trades found</h4>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                bulkTrades.map((trade) => (
                                                    <tr key={trade.bulkOrderId}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className={`fa fa-${trade.type === 'crypto' ? 'bitcoin' : 'dollar-sign'} text-primary me-2`}></i>
                                                                <div>
                                                                    <strong>{trade.bulkOrderId}</strong>
                                                                    <div className="text-muted fs-12">
                                                                        Created: {new Date(trade.createdAt).toLocaleDateString()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <Badge bg={trade.type === 'crypto' ? 'warning' : 'info'}>
                                                                    {trade.type.toUpperCase()}
                                                                </Badge>
                                                                <div className="text-muted mt-1">{trade.symbol}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="text-primary">{trade.openValue}</span>
                                                        </td>
                                                        <td>
                                                            {trade.closeValue ? (
                                                                <span className="text-primary">{trade.closeValue}</span>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Badge bg={getStatusBadge(trade.status)}>
                                                                {trade.status.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className={trade.summary?.totalProfitLoss >= 0 ? 'text-success' : 'text-danger'}>
                                                                {trade.summary?.totalProfitLoss?.toFixed(2) || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                <Button
                                                                    variant="info"
                                                                    size="sm"
                                                                    className="btn-icon-xxs"
                                                                    onClick={() => fetchBulkTradeDetails(trade.bulkOrderId)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye"></i>
                                                                </Button>
                                                                {trade.status === 'active' && (
                                                                    <Button
                                                                        variant="warning"
                                                                        size="sm"
                                                                        className="btn-icon-xxs"
                                                                        onClick={() => {
                                                                            setCloseFormData({
                                                                                bulkOrderId: trade.bulkOrderId,
                                                                                closeValue: '',
                                                                                closeDate: new Date().toISOString().split('T')[0],
                                                                                closeTime: new Date().toTimeString().slice(0, 8)
                                                                            });
                                                                            setShowCloseModal(true);
                                                                        }}
                                                                        title="Close Trade"
                                                                    >
                                                                        <i className="fa fa-times-circle"></i>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {!loading && Array.isArray(bulkTrades) && bulkTrades.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                    </div>
                                    <div>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            className="me-2" 
                                            disabled={pagination.page === 1}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        >
                                            Previous
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Create Trade Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Create Bulk Trade</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateTrades}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="crypto">Crypto</option>
                                        <option value="forex">Forex</option>
                                        <option value="gold">Gold</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Symbol</Form.Label>
                                    <Form.Select
                                        name="symbol"
                                        value={formData.symbol}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Symbol</option>
                                        {symbols.map(symbol => (
                                            <option key={symbol._id} value={symbol.name}>
                                                {symbol.name} {symbol.baseAsset ? `(${symbol.baseAsset})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Open Value</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="openValue"
                                        value={formData.openValue}
                                        onChange={handleInputChange}
                                        required
                                        step="any"
                                        placeholder="Enter open value"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Open Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="openDate"
                                        value={formData.openDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Open Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="openTime"
                                        value={formData.openTime}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-4" />
                        <h5 className="mb-3">User Trades</h5>

                        {formData.userTrades.map((trade, index) => (
                            <div key={index} className="mb-4 p-3 border rounded">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Trade #{index + 1}</h6>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleUserTradeRemove(index)}
                                    >
                                        <i className="fa fa-times"></i>
                                    </Button>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>User</Form.Label>
                                            <Form.Select
                                                value={trade.userId}
                                                onChange={(e) => handleUserTradeChange(index, 'userId', e.target.value)}
                                                required
                                            >
                                                <option value="">Select user</option>
                                                {users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name || user.email} {user.mobileNumber ? `(${user.mobileNumber})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={trade.quantity}
                                                onChange={(e) => handleUserTradeChange(index, 'quantity', e.target.value)}
                                                required
                                                step="any"
                                                placeholder="Enter quantity"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Direction</Form.Label>
                                            <Form.Select
                                                value={trade.direction}
                                                onChange={(e) => handleUserTradeChange(index, 'direction', e.target.value)}
                                                required
                                            >
                                                <option value="buy">Buy</option>
                                                <option value="sell">Sell</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        ))}

                        <Button
                            variant="success"
                            type="button"
                            onClick={handleUserTradeAdd}
                            className="w-100"
                        >
                            <i className="fa fa-plus me-2"></i>Add Trade
                        </Button>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={processing || formData.userTrades.length === 0}
                        >
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Creating...
                                </>
                            ) : (
                                'Create Trades'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Close Trade Modal */}
            <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Close Bulk Trade</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCloseTrades}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Bulk Order ID</Form.Label>
                            <Form.Control
                                type="text"
                                name="bulkOrderId"
                                value={closeFormData.bulkOrderId}
                                onChange={(e) => setCloseFormData(prev => ({
                                    ...prev,
                                    bulkOrderId: e.target.value
                                }))}
                                required
                                placeholder="Enter Bulk Order ID"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Close Value</Form.Label>
                            <Form.Control
                                type="number"
                                name="closeValue"
                                value={closeFormData.closeValue}
                                onChange={(e) => setCloseFormData(prev => ({
                                    ...prev,
                                    closeValue: e.target.value
                                }))}
                                required
                                step="any"
                                placeholder="Enter close value"
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Close Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="closeDate"
                                        value={closeFormData.closeDate}
                                        onChange={(e) => setCloseFormData(prev => ({
                                            ...prev,
                                            closeDate: e.target.value
                                        }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Close Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="closeTime"
                                        value={closeFormData.closeTime}
                                        onChange={(e) => setCloseFormData(prev => ({
                                            ...prev,
                                            closeTime: e.target.value
                                        }))}
                                        required
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Closing...
                                </>
                            ) : (
                                'Close Trades'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Details Modal */}
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Bulk Trade Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : selectedBulkTrade ? (
                        <div className="row">
                            <div className="col-12 mb-4">
                                <div className="d-flex align-items-center">
                                    <i className={`fa fa-${selectedBulkTrade.type === 'crypto' ? 'bitcoin' : 'dollar-sign'} fa-2x text-primary me-3`}></i>
                                    <div>
                                        <h4 className="mb-1">{selectedBulkTrade.symbol}</h4>
                                        <p className="mb-0 text-muted">Bulk Order ID: {selectedBulkTrade.bulkOrderId}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <Card>
                                    <Card.Header>
                                        <h5 className="card-title mb-0">Trade Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Type</strong>
                                                <Badge bg={selectedBulkTrade.type === 'crypto' ? 'warning' : 'info'}>
                                                    {selectedBulkTrade.type.toUpperCase()}
                                                </Badge>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Status</strong>
                                                <Badge bg={getStatusBadge(selectedBulkTrade.status)}>
                                                    {selectedBulkTrade.status.toUpperCase()}
                                                </Badge>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Open Value</strong>
                                                <span className="text-primary">{selectedBulkTrade.openValue}</span>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Close Value</strong>
                                                {selectedBulkTrade.closeValue ? (
                                                    <span className="text-primary">{selectedBulkTrade.closeValue}</span>
                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Open Date</strong>
                                                <span>{selectedBulkTrade.openManualDate || selectedBulkTrade.openDate} {selectedBulkTrade.openManualTime || ''}</span>
                                            </Col>
                                            {selectedBulkTrade.closeDate && (
                                                <Col lg={6} md={6} sm={12} className="mb-2">
                                                    <strong className="d-block text-dark">Closed At</strong>
                                                    <span>{selectedBulkTrade.closeManualDate || selectedBulkTrade.closeDate} {selectedBulkTrade.closeManualTime || ''}</span>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </div>

                            <div className="col-md-6">
                                <Card>
                                    <Card.Header>
                                        <h5 className="card-title mb-0">Summary</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Total Trades</strong>
                                                <span>{selectedBulkTrade.summary?.totalTrades || 0}</span>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Active Trades</strong>
                                                <span>{selectedBulkTrade.summary?.activeTrades || 0}</span>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Completed Trades</strong>
                                                <span>{selectedBulkTrade.summary?.completedTrades || 0}</span>
                                            </Col>
                                            <Col lg={6} md={6} sm={12} className="mb-2">
                                                <strong className="d-block text-dark">Total P/L</strong>
                                                <span className={selectedBulkTrade.summary?.totalProfitLoss >= 0 ? 'text-success' : 'text-danger'}>
                                                    {selectedBulkTrade.summary?.totalProfitLoss?.toFixed(2) || '0.00'}
                                                </span>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </div>

                            <div className="col-12 mt-4">
                                <Card>
                                    <Card.Header>
                                        <h5 className="card-title mb-0">Individual Trades</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="table-responsive">
                                            <Table className="table table-responsive-md">
                                                <thead>
                                                    <tr>
                                                        <th><strong>User</strong></th>
                                                        <th><strong>Direction</strong></th>
                                                        <th><strong>Quantity</strong></th>
                                                        <th><strong>Open Value</strong></th>
                                                        <th><strong>Close Value</strong></th>
                                                        <th><strong>P/L</strong></th>
                                                        <th><strong>Status</strong></th>
                                                        <th><strong>Actions</strong></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedBulkTrade.trades && Array.isArray(selectedBulkTrade.trades) && selectedBulkTrade.trades.map((trade) => (
                                                        <tr key={trade._id}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <i className="fa fa-user text-primary me-2"></i>
                                                                    <div>
                                                                        <strong>{trade.userId?.name || 'N/A'}</strong>
                                                                        <div className="text-muted fs-12">{trade.userId?.email || ''}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Badge bg={getDirectionBadge(trade.direction)}>
                                                                    {trade.direction.toUpperCase()}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <span className="text-primary">{trade.quantity}</span>
                                                            </td>
                                                            <td>
                                                                <span className="text-primary">{trade.openValue || trade.entryPrice}</span>
                                                            </td>
                                                            <td>
                                                                {trade.closeValue || trade.exitPrice ? (
                                                                    <span className="text-primary">{trade.closeValue || trade.exitPrice}</span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className={trade.profitLoss >= 0 ? 'text-success' : 'text-danger'}>
                                                                    {trade.profitLoss?.toFixed(2) || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <Badge bg={getStatusBadge(trade.status)}>
                                                                    {trade.status}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="btn-icon-xxs"
                                                                    onClick={() => handleDeleteIndividualTrade(trade._id)}
                                                                    title="Delete"
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p>No trade details available</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManualTrades;
